import type { AgenticLogicRulePublishJobDto } from "@intelligems/ig-types/dtos/agentic-logic/publish";
import { GateProgressStrip } from "@src/features/agenticCodegen/workspace/components/02-GateRail/GateProgressStrip";
import { AGENTIC_GATES_CONFIG } from "@src/features/ai/pages/agentic-logic/workspace/agenticDomain";

interface TemplatePublishStatusProps {
  job: AgenticLogicRulePublishJobDto;
  /**
   * `true` when this session started or watched the run. A success this
   * session did not run is history hydrated from rule detail — announcing it
   * would make the completed run permanent chrome on every applied
   * template rule. A failure stays reported either way: it is still actionable.
   */
  isSessionRun: boolean;
  /**
   * Accepted for existing callers. Gate checks use the same presentation for
   * standard and advanced users.
   */
  advancedEnabled: boolean;
}

const failedPublishMessage = (job: AgenticLogicRulePublishJobDto): string => {
  const result = job.gates.find((gate) => gate.status === "failed")?.result;
  const recovery = "Fix the configuration and save it again to retry.";
  return result ? `${result} ${recovery}` : recovery;
};

/**
 * Every run state uses the shared gate strip so its checks remain available.
 */
export const TemplatePublishStatus = ({
  job,
  isSessionRun,
}: TemplatePublishStatusProps) => {
  if (job.status === "succeeded" && !isSessionRun) {
    return null;
  }

  return (
    <GateProgressStrip
      gates={job.gates}
      config={AGENTIC_GATES_CONFIG}
      heading="Rule checks"
      isPublishing={job.status === "queued" || job.status === "running"}
      operation={{
        phase:
          job.status === "succeeded"
            ? "checked"
            : job.status === "failed"
              ? "failed"
              : job.status === "cancelled"
                ? "cancelled"
                : "checking",
        checksOnly: true,
        detail: job.status === "failed" ? failedPublishMessage(job) : undefined,
      }}
      horizontalMargin={0}
      topMargin={0}
      bottomMargin={0}
    />
  );
};
